import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Stack,
  Chip,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DatePicker } from "@mui/x-date-pickers";
import LineChart from "components/chartNivo/LineChart";


const MLTrainingSection = ({
  isModelReady,
  isTraining,
  trainingLogs,
  epochs,
  onTrain,
  onReset,
  description,
  showMae    = false,
  leftLegend = "Loss (MSE)",
  readyExtra = null,
}) => {
  const theme = useTheme();

  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setFullYear(d.getFullYear() - 1); return d;
  });
  const [endDate, setEndDate] = useState(new Date());

  const currentEpoch = trainingLogs.length;
  const lastLog      = trainingLogs[trainingLogs.length - 1];

  const lossChartData = trainingLogs.length > 1 ? [
    { id: "Training Loss",   data: trainingLogs.map((l) => ({ x: l.epoch, y: l.loss })) },
    { id: "Validation Loss", data: trainingLogs.map((l) => ({ x: l.epoch, y: l.val_loss })) },
  ] : [];


  return (
    <Stack spacing={2}>
      {/* Training Data Range */}
      <Box 
        component="fieldset" 
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
      >
        <Typography 
          component="legend" 
          variant="subtitle1" 
          sx={{ px: 1 }}
        >
          Training Data Range
        </Typography>

        <Stack 
          direction={{ xs: "column", sm: "row" }} 
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }} 
          justifyContent="center"
        >
          <DatePicker
            label="From Date"
            value={startDate}
            maxDate={endDate || new Date()}
            format="dd/MM/yyyy"
            onChange={(val) => setStartDate(val)}
            slotProps={{
              field: { clearable: true },
              openPickerButton: { size: 'small', sx: { p: '2px' } },
              clearButton: { size: 'small', sx: { p: '2px' } },
              textField: { variant: "outlined", size: "small", fullWidth: true },
            }}
            sx={{ width: { xs: "100%", sm: 220 } }}
          />
          <DatePicker
            label="To Date"
            value={endDate}
            minDate={startDate}
            maxDate={new Date()}
            format="dd/MM/yyyy"
            onChange={(val) => setEndDate(val)}
            slotProps={{
              field: { clearable: true },
              openPickerButton: { size: 'small', sx: { p: '2px' } },
              clearButton: { size: 'small', sx: { p: '2px' } },
              textField: { variant: "outlined", size: "small", fullWidth: true },
            }}
            sx={{ width: { xs: "100%", sm: 220 } }}
          />
          <Button 
            variant="outlined" 
            size="medium"
            startIcon={<CloudDownloadIcon />} 
            disabled={isTraining} 
            onClick={() => onTrain(startDate, endDate)}
            sx={{ whiteSpace: "nowrap", width: { xs: "100%", sm: "auto" } }}
          >
            {isTraining ? "Training..." : "Load & Train"}
          </Button>
          {isModelReady && (
            <Button 
              variant="outlined" 
              color="error" 
              size="medium" 
              startIcon={<DeleteOutlineIcon />}
              disabled={isTraining} 
              onClick={onReset}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Reset
            </Button>
          )}
        </Stack>
      </Box>

      {/* Progress */}
      {isTraining && (
        <Box>
          <Stack 
            direction={{ xs: "column", sm: "row" }} 
            justifyContent="space-between" 
            mb={0.5} 
            spacing={0.25}
          >
            <Typography 
              variant="body2" 
              color="text.secondary"
            >
              Epoch {currentEpoch} / {epochs}
            </Typography>
            {lastLog && (
              <Typography 
                variant="body2" 
                color="text.secondary"
              >
                Loss: {lastLog.loss.toFixed(5)} | Val: {lastLog.val_loss.toFixed(5)}
              </Typography>
            )}
          </Stack>
          <LinearProgress 
            variant="determinate" 
            value={(currentEpoch / epochs) * 100} 
            sx={{ height: 8, borderRadius: 4 }} 
          />
        </Box>
      )}

      {/* Ready */}
      {isModelReady && !isTraining && (
        <Alert severity="success" icon={false}>
          <Stack 
            direction={{ xs: "column", sm: "row" }} 
            spacing={1}
            alignItems={{ xs: "flex-start", sm: "center" }} 
            justifyContent="center" 
            flexWrap="wrap"
          >
            <Typography 
              variant="body2" 
              fontWeight={600}
            >
              Model ready
            </Typography>
            {lastLog && (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {showMae && (
                  <Chip 
                    variant="outlined" 
                    size="small" 
                    color="success" 
                    label={`MAE: ${lastLog.mae?.toFixed(4)}`} 
                  />
                )}
                <Chip 
                  variant="outlined" 
                  size="small" 
                  color="info"    
                  label={`Val Loss: ${lastLog.val_loss.toFixed(5)}`} 
                />
              </Stack>
            )}
            {readyExtra}
          </Stack>
        </Alert>
      )}

      {/* Loss chart */}
      {lossChartData.length > 0 && (
        <Box>
          <Typography 
            variant="subtitle1" 
            mb={1} 
            textAlign="center"
          >
            Training Progress (Loss Curve)
          </Typography>
          <Box height={{ xs: 220, sm: 280 }}>
            <LineChart 
              data={lossChartData} 
              bottomLegend="Epoch" 
              leftLegend={leftLegend} 
            />
          </Box>
        </Box>
      )}

      {/* Empty state */}
      {!isModelReady && !isTraining && description && (
        <Alert severity="info">{description}</Alert>
      )}
    </Stack>
  );
};

export default MLTrainingSection;
